
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PageHeader, Tabs } from '../../../components/ds';
import { FiActivity, FiClock, FiList, FiPlay, FiLayout } from "react-icons/fi";
import { services } from '../../../services/api';
import WebSocketManager from '../../../websocket/WebSocketManager';
import { Header } from "../../../components";
import Loader from "../../../components/Loader";
import { useStateContext } from "../../../contexts/ContextProvider";
import JobsTable from './components/JobsTable';
import JobRunsTable from './components/JobRunsTable';
import DashboardStats from './components/DashboardStats';

const BatchJobs = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [runs, setRuns] = useState([]);
  
  // Track running jobs for UI feedback
  const [runningJobs, setRunningJobs] = useState(new Set());
  
  // WebSocket subscription ref
  const wsSubscriptionRef = useRef(null);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await services.jobOrchestrator.getDashboard();
      setDashboard(response.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
      setError('Failed to load dashboard');
    }
  }, []);

  // Fetch all jobs
  const fetchJobs = useCallback(async () => {
    try {
      const response = await services.jobOrchestrator.listJobs();
      setJobs(response.data.jobs || []);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setError('Failed to load jobs');
    }
  }, []);

  // Fetch all runs
  const fetchRuns = useCallback(async () => {
    try {
      const response = await services.jobOrchestrator.listAllRuns({ limit: 50 });
      setRuns(response.data.runs || []);
    } catch (err) {
      console.error('Failed to fetch runs:', err);
    }
  }, []);

  // Handle WebSocket job run updates
  const handleJobRunUpdate = useCallback((data) => {
    // console.log('📡 [BatchJobs] WebSocket job update:', data);
    
    if (!data || !data.event) return;
    
    const { event, job_id, job_name, status, run_id } = data;
    
    if (event === 'job_started') {
      // Add to running jobs set
      setRunningJobs(prev => new Set([...prev, job_id]));
      
      // Add new run to the list (at the top)
      setRuns(prev => [{
        id: run_id,
        job_name,
        status: 'running',
        started_at: data.started_at,
        triggered_by: data.triggered_by,
        triggered_by_user: data.triggered_by_user,
      }, ...prev.slice(0, 49)]);
      
    } else if (event === 'job_completed') {
      // Remove from running jobs
      setRunningJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(job_id);
        return newSet;
      });
      
      // Update the run in the list
      setRuns(prev => prev.map(run => 
        run.id === run_id
          ? {
              ...run,
              status,
              completed_at: data.completed_at,
              duration_ms: data.duration_ms,
              response_status_code: data.response_status_code,
              error_message: data.error_message,
            }
          : run
      ));
      
      // Update job's status and next run time from WebSocket event
      setJobs(prev => prev.map(job => 
        job.id === job_id
          ? { 
              ...job, 
              last_run_status: status, 
              last_run_at: data.completed_at || data.last_run_at,
              next_run_at: data.next_run_at  // Update next run time live!
            }
          : job
      ));
      
      // Refresh dashboard stats
      fetchDashboard();
    }
  }, [fetchDashboard]);

  // Initial data load + persistent WebSocket subscription + visibility handler
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchJobs(), fetchRuns()]);
      setLoading(false);
    };
    loadData();
    
    // Subscribe to job-orchestrator.runs for live updates
    const subscription = 'job-orchestrator.runs';
    console.log('📡 [BatchJobs] Subscribing to:', subscription);
    
    const handlerObj = {
      onData: handleJobRunUpdate,
      onAck: (data) => console.log('📡 [BatchJobs] Subscription ACK:', data),
      onError: (err) => console.error('📡 [BatchJobs] Subscription error:', err)
    };
    
    const unsubscribe = WebSocketManager.subscribeTopic(subscription, handlerObj);
    wsSubscriptionRef.current = { subscription, handlerObj, unsubscribe };
    
    // Auto-refresh on visibility change (handle tab resume)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ [BatchJobs] Tab visible, refreshing data...');
        Promise.all([fetchDashboard(), fetchJobs(), fetchRuns()]);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      // Cleanup
      if (wsSubscriptionRef.current?.unsubscribe) {
        console.log('📡 [BatchJobs] Unsubscribing from:', wsSubscriptionRef.current.subscription);
        wsSubscriptionRef.current.unsubscribe();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchDashboard, fetchJobs, fetchRuns, handleJobRunUpdate]);

  // Trigger a job manually
  const handleTriggerJob = async (jobId) => {
    // Check if job is already running
    if (runningJobs.has(jobId)) {
      setError('This job is already running');
      return;
    }
    
    try {
      // Optimistically add to running set
      setRunningJobs(prev => new Set([...prev, jobId]));
      
      await services.jobOrchestrator.triggerJob(jobId);
      // Don't need to refresh - WebSocket will update us
    } catch (err) {
      console.error('Failed to trigger job:', err);
      setError('Failed to trigger job');
      // Remove from running on error
      setRunningJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  // Toggle job enabled/disabled
  const handleToggleJob = async (jobId, enabled) => {
    try {
      if (enabled) {
        await services.jobOrchestrator.enableJob(jobId);
      } else {
        await services.jobOrchestrator.disableJob(jobId);
      }
      await fetchJobs();
    } catch (err) {
      console.error('Failed to toggle job:', err);
      setError('Failed to toggle job');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader text="Loading batch jobs..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-1 pt-0 px-4 md:px-6 pb-10">
      <div className="space-y-6">

        {/* Standardized page header (common across all pages) */}
        <PageHeader
          title="Batch Jobs"
          subtitle="Orchestrate, schedule, and monitor background tasks across your microservices ecosystem."
          actions={runningJobs.size > 0 && (
            <div className="flex items-center gap-3 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full animate-pulse">
              <div className="relative">
                <div className="w-2.5 h-2.5 bg-accent rounded-full animate-ping absolute" />
                <div className="w-2.5 h-2.5 bg-accent rounded-full relative" />
              </div>
              {/* text-blue-300 had no `dark:` — pale blue on a near-white light theme. */}
              <span className="text-sm font-medium text-accent">
                {runningJobs.size} Job{runningJobs.size > 1 ? 's' : ''} Running
              </span>
            </div>
          )}
        />

        {/* Error Alert — text-red-200 had no `dark:`, so the error message was
            pale pink on a near-white light theme: unreadable exactly when it
            matters. Badge tokens are theme-aware in both directions. */}
        {error && (
          <div className="p-4 bg-[var(--badge-loss-bg)] border border-[var(--divider)] rounded-xl text-[var(--badge-loss-fg)] flex items-center justify-between animate-fadeIn backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <span className="text-xl">⚠️</span>
              <span>{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="p-1 hover:bg-loss/20 rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
        )}

        {/* Navigation Tabs */}
        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          tabs={[
            { label: 'Dashboard', value: 'dashboard', icon: <FiLayout /> },
            { label: 'Scheduled Jobs', value: 'jobs', icon: <FiClock />, badge: jobs.length },
            { label: 'Execution History', value: 'history', icon: <FiList />, badge: runs.length },
          ]}
        />

        {/* Tab Content Area */}
        <div className="min-h-[500px] transition-all duration-300 ease-in-out">
          {activeTab === 'dashboard' && dashboard && (
            <div className="animate-fadeIn">
              <DashboardStats 
                dashboard={dashboard} 
                onRefresh={fetchDashboard}
              />
            </div>
          )}

          {activeTab === 'jobs' && (
            <div className="animate-fadeIn">
              <JobsTable 
                jobs={jobs} 
                runningJobs={runningJobs}
                onTrigger={handleTriggerJob}
                onToggle={handleToggleJob}
                onRefresh={fetchJobs}
              />
            </div>
          )}

          {activeTab === 'history' && (
            <div className="animate-fadeIn">
              <JobRunsTable 
                runs={runs}
                onRefresh={fetchRuns}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchJobs;

